// Ayuda del módulo Broadnet: guía práctica orientada al flujo de procesos, con
// checklist "listo para operar" en vivo y enlaces directos a cada pantalla.
// POS → Broadnet → Ayuda.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, BadgeCheck, BookOpen, CalendarClock, CheckCircle2, ChevronDown, ChevronRight,
  ExternalLink, Loader2, MinusCircle, XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { bnAyudaEstado, money, type BnAyudaEstado } from './shared';

// ---- navegación por pestañas (deep-link dentro del SPA) ----
function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}
function Ir({ to, title, children }: { to: string; title: string; children?: React.ReactNode }) {
  const ir = useIr();
  return (
    <button onClick={() => ir(to, title)}
      className="inline-flex items-center gap-1 text-primary hover:underline text-sm font-medium">
      {children ?? title} <ExternalLink className="w-3.5 h-3.5" />
    </button>
  );
}

// ---- prerrequisitos verificados contra la BD ----
// El flag 'worker' es advisory: en modo simulado no aplica (no bloquea).
const CHECKS: { key: keyof BnAyudaEstado['checklist']; label: string; ruta: string; titulo: string; ayuda: string }[] = [
  { key: 'config', label: 'Broadnet configurado (conexión activa)', ruta: '/pos/broadnet/conexion', titulo: 'Conexión', ayuda: 'Modo (simulado/producción) y datos de conexión.' },
  { key: 'modo_listo', label: 'Modo definido (en producción, URL del WebService cargada)', ruta: '/pos/broadnet/conexion', titulo: 'Conexión', ayuda: 'En producción no se puede operar sin la URL del WebService.' },
  { key: 'productos', label: 'Al menos un producto/servicio activo', ruta: '/pos/broadnet/productos', titulo: 'Productos y Servicios', ayuda: 'Recargas y/o servicios básicos habilitados para vender.' },
  { key: 'terminal', label: 'Terminal por caja con TID/MID/clave', ruta: '/pos/broadnet/terminales', titulo: 'Terminales', ayuda: 'Cada caja que venda recargas necesita su propia terminal registrada.' },
  { key: 'codigos', label: 'Reglas de reverso cargadas', ruta: '/pos/broadnet/codigos', titulo: 'Reglas de reverso', ayuda: 'Definen qué hacer con cada código de respuesta del proveedor.' },
  { key: 'efectivo', label: 'Forma de pago EFECTIVO habilitada (las recargas son solo efectivo)', ruta: '/pos/formas-pago', titulo: 'Formas de pago', ayuda: 'Broadnet no admite recargas con tarjeta ni otras formas de pago.' },
  { key: 'worker', label: 'Worker de producción activo', ruta: '/pos/broadnet/monitoreo', titulo: 'Monitoreo', ayuda: 'Solo aplica en modo producción; en modo simulado no hace falta.' },
];

// ---- el flujo de Broadnet ----
const FLUJO: { n: number; titulo: string; detalle: string; ruta: string; tituloTab: string }[] = [
  { n: 1, titulo: 'Configurar la conexión', detalle: 'Elige el modo (simulado o producción), la URL del WebService y las credenciales.', ruta: '/pos/broadnet/conexion', tituloTab: 'Conexión' },
  { n: 2, titulo: 'Activar productos y servicios', detalle: 'Recargas móviles/TV y servicios básicos (agua, luz, teléfono) que vas a ofrecer.', ruta: '/pos/broadnet/productos', tituloTab: 'Productos y Servicios' },
  { n: 3, titulo: 'Registrar la terminal de cada caja', detalle: 'TID, MID y clave: cada caja que venda recargas necesita su propia terminal.', ruta: '/pos/broadnet/terminales', tituloTab: 'Terminales' },
  { n: 4, titulo: 'Cargar las reglas de reverso', detalle: 'Qué hacer según el código de respuesta del proveedor (aprobar, reversar, reintentar).', ruta: '/pos/broadnet/codigos', tituloTab: 'Reglas de reverso' },
  { n: 5, titulo: 'Vender recargas / cobrar servicios básicos', detalle: 'Desde POS → Venta/Caja → Recargas (Broadnet), o desde el Punto de Venta con el atajo de caja.', ruta: '/pos/recargas', tituloTab: 'Recargas (Broadnet)' },
  { n: 6, titulo: 'Monitorear y regularizar pendientes', detalle: 'Revisa transacciones, trazabilidad del WebService y regulariza los reversos pendientes.', ruta: '/pos/broadnet/monitoreo', tituloTab: 'Monitoreo' },
];

// ---- cómo hago... ----
const COMO: { q: string; pasos: string[]; enlaces: { to: string; t: string }[] }[] = [
  { q: '¿Cómo vendo una recarga?', pasos: ['Ve a POS → Venta/Caja → Recargas (Broadnet), o desde el Punto de Venta con el atajo de caja.', 'Elige el producto/servicio, el celular y el monto.', 'Cobra: las recargas se pagan SOLO en efectivo.'], enlaces: [{ to: '/pos/recargas', t: 'Recargas (Broadnet)' }] },
  { q: '¿Qué hago con un "reverso pendiente"?', pasos: ['La recarga no tuvo respuesta clara del proveedor y queda pendiente.', 'Ve a Monitoreo (o Recargas → Transacciones) y usa el botón Regularizar en la fila.', 'Regularizar la deja en "reversada" (NO entró → no cobrar / devolver el efectivo) o "confirmada" (SÍ entró → venta válida).', 'Mientras esté pendiente, esa caja queda bloqueada: no puede vender otra recarga ni cerrar turno.', 'En producción el worker la resuelve solo; en modo simulado se resuelve al instante.', 'Si Regularizar no hace nada por más que insistas, comprueba primero que el worker esté EN LÍNEA (indicador arriba de esta pantalla): sin worker, regularizar sólo avisa al vacío y la caja no se desbloquea jamás.'], enlaces: [{ to: '/pos/broadnet/monitoreo', t: 'Monitoreo' }] },
  { q: '¿Y si Broadnet nunca recibió la recarga?', pasos: ['Pasa cuando la petición no llegó a salir: la transacción queda pendiente para siempre y bloquea la caja.', 'Antes de nada, CONFIRMA CON BROADNET que esa referencia no existe de su lado.', 'Con esa confirmación, un supervisor (nivel 4 o superior) puede usar el botón «Descartar» de la fila, escribiendo el motivo. La transacción pasa a estado terminal y la caja queda libre para vender y cerrar.', 'El sistema se NIEGA a descartar cualquier recarga que tenga algún rastro de respuesta del proveedor —autorización, código, tiquete o nota de venta—: ésa se reversa o se confirma, nunca se da por perdida.', 'Tampoco deja descartar una recarga de hace menos de 30 minutos: puede estar resolviéndose.'], enlaces: [{ to: '/pos/broadnet/monitoreo', t: 'Monitoreo' }] },
  { q: '¿Cómo paso a producción?', pasos: ['En Conexión, cambia el Modo a "WebService (producción)" y carga la URL y las credenciales.', 'En Terminales, registra TID/MID/clave de cada caja.', 'Requiere el worker de producción activo y red/VPN hacia Broadnet.'], enlaces: [{ to: '/pos/broadnet/conexion', t: 'Conexión' }, { to: '/pos/broadnet/terminales', t: 'Terminales' }] },
  { q: '¿Quién factura la recarga?', pasos: ['En Conexión eliges el valor POR DEFECTO de las recargas: "comercio" = emites factura SRI desde tu POS; "broadnet" = ellos facturan y tú entregas un voucher.', 'En Productos, cada operadora y cada servicio tiene una columna «¿Quién factura?» con tres opciones: Heredar (lo de Conexión), El comercio, o Broadnet. Debajo se lee lo que va a pasar de verdad.', 'Los servicios básicos heredan SIEMPRE "recibo", aunque Conexión diga "comercio": una recaudación no es una venta y el dinero no es tuyo. Si tienes un acuerdo distinto con algún catálogo, márcalo en ese servicio a mano.', 'Cuando factura el comercio, la factura sale sobre un producto de servicio propio de esa operadora (REC-…) o de ese servicio (SRV-…), que se crea solo la primera vez — así los reportes se desglosan.'], enlaces: [{ to: '/pos/broadnet/conexion', t: 'Conexión' }, { to: '/pos/broadnet/productos', t: 'Productos' }] },
  { q: '¿Cómo concilio con Broadnet?', pasos: ['Ve a Monitoreo → pestaña Transacciones → Exportar.', 'Cruza Referencia + Autorización contra el Excel diario que entrega el proveedor.', 'Tienes 48 horas para reclamar inconsistencias.'], enlaces: [{ to: '/pos/broadnet/monitoreo', t: 'Monitoreo' }] },
  { q: '¿Cómo veo la trazabilidad del WebService?', pasos: ['Ve a Monitoreo → pestaña Trazabilidad.', 'Elige una transacción y revisa la línea de tiempo de las llamadas al WebService (petición, reverso, consulta) con sus tiempos y códigos de respuesta.'], enlaces: [{ to: '/pos/broadnet/monitoreo', t: 'Monitoreo' }] },
];

// ---- glosario ----
const GLOSARIO: { t: string; d: string }[] = [
  { t: 'Recarga', d: 'Carga de saldo a un número celular (prepago) o de un servicio de TV/streaming, a través del proveedor Broadnet.' },
  { t: 'Servicio básico', d: 'Pago de agua, luz o teléfono fijo consultado y pagado vía Broadnet; siempre lo factura el proveedor.' },
  { t: 'Reverso', d: 'Anulación de una operación que no se confirmó del lado del proveedor; puede ser automático o manual (regularizar).' },
  { t: 'Descartar', d: 'Dar por NO ENVIADA una recarga que nunca llegó a Broadnet, para liberar la caja. Exige supervisor y motivo, y sólo se admite si no hay ni un rastro de respuesta del proveedor.' },
  { t: 'Worker en línea', d: 'El proceso que habla con el WebService de Broadnet. Sin él, ninguna recarga sale ni se regulariza: el indicador de esta pantalla debe estar en verde.' },
  { t: 'Confirmación', d: 'Respuesta definitiva del proveedor sobre si la recarga/pago realmente se acreditó.' },
  { t: 'Terminal (TID/MID)', d: 'Identificadores de la caja ante Broadnet: Terminal ID y Merchant ID, más una clave; una terminal por caja.' },
  { t: 'Reglas de reverso', d: 'Catálogo de códigos de respuesta del proveedor con la acción que debe tomar el sistema (ok/reverso/reintento/confirmar/error).' },
  { t: 'Modo simulado / producción', d: 'Simulado: no llama al proveedor real, para probar sin riesgo. Producción: llama al WebService real de Broadnet.' },
  { t: 'Regularizar', d: 'Acción manual (o automática del worker) que cierra una transacción pendiente como reversada o confirmada.' },
  { t: 'Trazabilidad', d: 'Registro detallado de cada llamada al WebService de Broadnet por transacción (bn_ws_log): fase, tiempos y códigos.' },
];

// ---- recordatorios operativos (Broadnet no tiene calendario fiscal propio) ----
const RECORDATORIOS: string[] = [
  'Tienes 48 horas para reclamar inconsistencias contra el Excel diario de Broadnet.',
  'No cierres el turno de caja con recargas pendientes de regularizar.',
  'Si el worker aparece fuera de línea, no vendas recargas: quedarán pendientes y bloquearán la caja.',
  'Las recargas se cobran SOLO en efectivo.',
];

function CheckItem({ ok, advisory, label, ruta, titulo, ayuda }: { ok: boolean; advisory?: boolean; label: string; ruta: string; titulo: string; ayuda: string }) {
  const icon = ok
    ? <CheckCircle2 className="w-5 h-5 text-estable shrink-0 mt-0.5" />
    : advisory
      ? <MinusCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
      : <XCircle className="w-5 h-5 text-urgente shrink-0 mt-0.5" />;
  return (
    <div className="flex items-start gap-3 py-2 border-b last:border-0">
      {icon}
      <div className="min-w-0 flex-1">
        <div className="text-sm">{label}</div>
        <div className="text-xs text-muted-foreground">{ayuda}</div>
      </div>
      {!ok && !advisory && <Ir to={ruta} title={titulo}>Configurar</Ir>}
      {!ok && advisory && <Ir to={ruta} title={titulo}>Revisar</Ir>}
    </div>
  );
}

function Acordeon({ q, pasos, enlaces }: { q: string; pasos: string[]; enlaces: { to: string; t: string }[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-lg">
      <button className="w-full flex items-center gap-2 px-4 py-3 text-left" onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
        <span className="text-sm font-medium">{q}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pl-10 space-y-2">
          <ol className="list-decimal space-y-1 text-sm text-muted-foreground ml-4">
            {pasos.map((p, i) => <li key={i}>{p}</li>)}
          </ol>
          <div className="flex flex-wrap gap-3 pt-1">
            {enlaces.map((e) => <Ir key={e.to} to={e.to} title={e.t} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export function AyudaBroadnet() {
  const { data, isFetching } = useQuery<BnAyudaEstado>({
    queryKey: ['bn-ayuda'],
    queryFn: bnAyudaEstado,
  });
  const ch = data?.checklist ?? ({} as BnAyudaEstado['checklist']);
  const simulado = data?.modo === 'simulado';
  // El check del worker es advisory: en modo simulado no bloquea (no aplica).
  const listos = CHECKS.filter((c) => ch[c.key] || (c.key === 'worker' && simulado)).length;

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div>
        <h1 className="text-xl font-semibold">Ayuda · Broadnet</h1>
        <p className="text-sm text-muted-foreground">Guía práctica del módulo: qué configurar primero, el flujo de venta de recargas y servicios básicos, cómo resolver pendientes y a dónde ir. Los enlaces abren la pantalla correspondiente.</p>
      </div>

      {/* Listo para operar */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-primary" /> Listo para operar ({listos}/{CHECKS.length})
            {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-2">Lo que debe estar definido antes de vender tu primera recarga. Lo verde ya está; lo rojo tiene un enlace para configurarlo.</p>
          {CHECKS.map((c) => (
            <CheckItem key={c.key} ok={!!ch[c.key]} advisory={c.key === 'worker' && simulado}
              label={c.label} ruta={c.ruta} titulo={c.titulo} ayuda={c.ayuda} />
          ))}
        </CardContent>
      </Card>

      {/* Estado actual */}
      {data && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Estado actual</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2 py-1 rounded-md text-sm font-medium ${data.modo === 'produccion' ? 'bg-estable/10 text-estable' : 'bg-muted text-muted-foreground'}`}>
                Modo: {data.modo === 'produccion' ? 'Producción' : data.modo === 'simulado' ? 'Simulado' : 'Sin definir'}
              </span>
              <span className="px-2 py-1 rounded-md bg-muted text-sm">
                Factura la recarga: {data.recarga_facturacion === 'broadnet' ? 'Broadnet (voucher)' : 'El comercio (factura SRI)'} · IVA {data.recarga_iva_tarifa}%
              </span>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <span>{data.productos_activos} producto(s)/servicio(s) activo(s)</span>
              <span>{data.terminales} terminal(es) registrada(s)</span>
              <span>Hoy: {data.ventas_hoy.n} venta(s) · {money(data.ventas_hoy.total)}</span>
            </div>
            {data.pendientes > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-urgente/30 bg-urgente/10 px-3 py-2">
                <AlertTriangle className="w-4 h-4 text-urgente shrink-0 mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium text-urgente">{data.pendientes} transacción(es) pendiente(s) de regularizar.</span>{' '}
                  Mientras haya una pendiente, esa caja no puede vender otra recarga ni cerrar turno.{' '}
                  <Ir to="/pos/broadnet/monitoreo" title="Monitoreo">Regularizar</Ir>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* El flujo de Broadnet */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarClock className="w-5 h-5 text-primary" /> El flujo de Broadnet</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {FLUJO.map((f) => (
            <div key={f.n} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center shrink-0">{f.n}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{f.titulo}</div>
                <div className="text-xs text-muted-foreground">{f.detalle}</div>
              </div>
              <Ir to={f.ruta} title={f.tituloTab}>Ir</Ir>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Cómo hago */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-5 h-5 text-primary" /> ¿Cómo hago...?</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {COMO.map((c) => <Acordeon key={c.q} {...c} />)}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Recordatorios operativos */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Recordatorios operativos</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {RECORDATORIOS.map((r, i) => (
              <div key={i} className="text-sm flex items-start gap-2">
                <span className="text-muted-foreground shrink-0">·</span>
                <span>{r}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Glosario */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Glosario</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {GLOSARIO.map((g) => (
              <div key={g.t} className="text-sm">
                <div className="font-medium">{g.t}</div>
                <div className="text-xs text-muted-foreground">{g.d}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
